import React, { useState, useRef, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import RecentVideosCarousel from '../components/RecentVideosCarousel';
import PromptInputTabs from '../components/PromptInputTabs';
import SubscriptionModal from '../components/SubscriptionModal';
import RightSideMenu from '../components/RightSideMenu';
import CreditStatus from '../components/CreditStatus';
import { authService } from '../services/authService';
import { useLanguage } from '../contexts/LanguageContext';
import { translations } from '../translations';
import { getCreditStatus } from '../services/api';
import UnifiedHeader from '../components/UnifiedHeader';
import { Video, Sparkles, Globe, Zap, AlertTriangle, CheckCircle, X, ShieldCheck, Info } from 'lucide-react';
import './Dashboard.css';

// ─── Responsibility Modal ────────────────────────────────────────────────────
function ResponsibilityModal({ isOpen, onAccept, onClose, t, showAsReminder }) {
  if (!isOpen) return null;

  return (
    <div className="resp-overlay" onClick={showAsReminder ? onClose : undefined}>
      <div className="resp-modal" onClick={(e) => e.stopPropagation()}>

        {/* Close button — only in reminder mode */}
        {showAsReminder && (
          <button className="resp-close" onClick={onClose} aria-label="Close">
            <X size={18} />
          </button>
        )}

        {/* Header */}
        <div className="resp-header">
          <div className="resp-header-icon">
            <ShieldCheck size={28} />
          </div>
          <div>
            <h2 className="resp-title">
              {t.contentCreator?.responsibilityTitle || 'Create with Accuracy & Responsibility'}
            </h2>
            <p className="resp-subtitle">
              {t.contentCreator?.responsibilityText ||
                'The quality of your video depends on the accuracy of your input. Please provide correct solutions and explanations.'}
            </p>
          </div>
        </div>

        {/* Checklist points */}
        <div className="resp-points">
          {[
            t.contentCreator?.point1,
            t.contentCreator?.point2,
            t.contentCreator?.point3,
            t.contentCreator?.point4,
            t.contentCreator?.point5,
          ]
            .filter(Boolean)
            .map((point, i) => (
              <div key={i} className="resp-point">
                <CheckCircle size={16} className="resp-point-icon" />
                <span>{point}</span>
              </div>
            ))}
        </div>

        {/* Additional notices */}
        <div className="resp-notices">
          <div className="resp-notice resp-notice--amber">
            <AlertTriangle size={14} />
            <span>{t.contentCreator?.privacyWarning || 'Never input personal or confidential information'}</span>
          </div>
          <div className="resp-notice resp-notice--green">
            <Info size={14} />
            <span>{t.contentCreator?.copyrightNotice || 'Copyright: You retain ownership of generated videos'}</span>
          </div>
          <div className="resp-notice resp-notice--blue">
            <ShieldCheck size={14} />
            <span>{t.contentCreator?.contentSafety || 'Content Safety: All generated content is monitored for age-appropriate material'}</span>
          </div>
        </div>

        {/* Matriculation badge */}
        <div className="resp-matric">
          <span>{t.contentCreator?.matricExamFocus || '🎓 Optimised for Finnish, Swedish & English Matriculation Exams'}</span>
        </div>

        {/* CTA */}
        <div className="resp-actions">
          {showAsReminder ? (
            <button className="resp-btn-primary" onClick={onAccept}>
              {t.contentCreator?.gotIt || 'Got it — continue'}
            </button>
          ) : (
            <>
              <p className="resp-agree-note">
                {t.contentCreator?.agreeNote ||
                  'By clicking below you confirm you have read and understood these guidelines.'}
              </p>
              <button className="resp-btn-primary" onClick={onAccept}>
                {t.contentCreator?.agreeBtn || 'I Understand — Start Creating'}
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

function Dashboard() {
  const navigate = useNavigate();
  const { language } = useLanguage();
  const t = translations[language];
  const [refreshKey, setRefreshKey] = useState(0);
  const [showSubscriptionModal, setShowSubscriptionModal] = useState(false);
  const [showRightMenu, setShowRightMenu] = useState(false);
  const creditStatusRef = useRef(null);
  const videosSectionRef = useRef(null);
  const user = authService.getUser();
  const [creditStatus, setCreditStatus] = useState(null);
  const [refreshTrigger, setRefreshTrigger] = useState(0);
  
  // ─── Responsibility Modal state ───────────────────────────────────────────
  const RESP_STORAGE_KEY = 'animlearn_resp_accepted';
  const [respModalOpen, setRespModalOpen] = useState(false);
  const [respModalMode, setRespModalMode] = useState('first-time'); // 'first-time' | 'reminder'

  const [pendingGenerate, setPendingGenerate] = useState(null);

  const handleRespAccept = () => {
    localStorage.setItem(RESP_STORAGE_KEY, '1');
    const fnToCall = pendingGenerate?.fn;
    setPendingGenerate(null);
    setRespModalOpen(false);
    if (fnToCall) {
      setTimeout(() => fnToCall(), 0);
    }
  };

  const handleRespClose = () => {
    setRespModalOpen(false);
    setPendingGenerate(null);
  };

  // Called by PromptInputTabs before every generate
  // NOTE: wrap in {fn} object — if you do setPendingGenerate(() => generateFn)
  // React treats the callback as a state updater and calls it immediately,
  // leaving pendingGenerate as undefined.
  const handleBeforeGenerate = useCallback((generateFn) => {
    setRespModalMode('reminder');
    setPendingGenerate({ fn: generateFn });
    setRespModalOpen(true);
  }, []);

  // Scroll to top when component mounts
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  // Show modal on first visit
  useEffect(() => {
    const accepted = localStorage.getItem(RESP_STORAGE_KEY);
    if (!accepted) {
      setRespModalMode('first-time');
      setRespModalOpen(true);
    }
  }, []);
  
  // Wrap loadCreditStatus in useCallback
  const loadCreditStatus = useCallback(async () => {
    try {      
      const status = await getCreditStatus();
      console.log('💰 Dashboard credit status loaded:', status);
      setCreditStatus(status);
      
      // Force update the CreditStatus component if it has a refresh method
      if (creditStatusRef.current && typeof creditStatusRef.current.refresh === 'function') {
        creditStatusRef.current.refresh();
      }
    } catch (err) {
      console.error('Error loading credit status:', err);
    }
  }, []);

  useEffect(() => {
    loadCreditStatus();
    
    // Listen for credit status change events
    const handleCreditChange = () => {
      console.log('💎 Credit status change event received in Dashboard');
      loadCreditStatus();
      setRefreshTrigger(prev => prev + 1);
    };
    
    window.addEventListener('creditStatusChanged', handleCreditChange);
    
    return () => {
      window.removeEventListener('creditStatusChanged', handleCreditChange);
    };
  }, [loadCreditStatus]);

  const handleVideoGenerated = () => {
    if (creditStatusRef.current) {
      creditStatusRef.current.refresh();
    }
    setRefreshKey(prev => prev + 1);
    
    // Scroll to videos section after video is queued
    setTimeout(() => {
      if (videosSectionRef.current) {
        videosSectionRef.current.scrollIntoView({ 
          behavior: 'smooth', 
          block: 'start' 
        });
      }
    }, 600);
  };

  const handleOpenSubscription = () => {
    setShowSubscriptionModal(true);
    document.body.classList.add('modal-open');
  };

  const handleCloseSubscription = () => {
    setShowSubscriptionModal(false);
    document.body.classList.remove('modal-open');
  };

  const handleSelectPlan = (plan) => {
    console.log('Selected plan:', plan);
  };

  return (
    <div className="dashboard">
      {/* Responsibility Modal */}
      <ResponsibilityModal
        isOpen={respModalOpen}
        onAccept={handleRespAccept}
        onClose={handleRespClose}
        t={t}
        showAsReminder={respModalMode === 'reminder'}
      />

      <UnifiedHeader
        onOpenMenu={() => setShowRightMenu(true)}
        rightContent={
          <CreditStatus
            key={refreshTrigger}
            ref={creditStatusRef}
            compact={true}
            onUpgrade={handleOpenSubscription}
          />
        }
      />

      {/* Main Content */}
      <main className="dashboard-main">
        <div className="dashboard-container">
          {/* Enhanced Page Header with All Info */}
          <div className="dashboard-page-header">
            <div className="header-content-wrapper">
              <div className="header-icon-section">
                <div className="creator-icon">
                  <Sparkles size={56} />
                </div>
              </div>
              
              <div className="header-text-section">
                <h1 className="page-title">{t.contentCreator?.title || 'Content Creator'}</h1>
                <p className="page-subtitle">
                  {t.contentCreator?.subtitle || 'Create educational videos with AI-powered animations for Chemistry, Physics, and Mathematics'}
                </p>
                
                <div className="header-features">
                  <div className="feature-badge">
                    <Video size={16} />
                    <span>{t.contentCreator?.feature1 || 'Animated Explanations'}</span>
                  </div>
                  <div className="feature-badge">
                    <Globe size={16} />
                    <span>{t.contentCreator?.feature2 || 'Multi-language Support'}</span>
                  </div>
                  <div className="feature-badge">
                    <Zap size={16} />
                    <span>{t.contentCreator?.feature3 || 'Fast Generation'}</span>
                  </div>
                </div>
              </div>
            </div>

			{/* NEW: Matriculation Exam Badge */}
			<div className="matric-exam-badge">
			  <span>{t.contentCreator?.matricExamFocus}</span>
			</div>
          </div>

          {/* Create Section */}
          <section className="create-section">
            <div className="create-content">

              <PromptInputTabs onVideoGenerated={handleVideoGenerated} onBeforeGenerate={handleBeforeGenerate} />
            </div>
          </section>

          {/* Recent Videos Carousel Section */}
          <section className="videos-section" ref={videosSectionRef}>
            <div className="section-header-main">
              <div className="section-title-group">
                <h2 className="section-title">{t.recentVideos || 'Recent Videos'}</h2>
                <p className="section-subtitle">{t.recentVideosDesc || 'Your recently created videos'}</p>
              </div>
              <button 
                onClick={() => navigate('/videos')} 
                className="btn-view-all-videos"
              >
                <Video size={18} />
                <span>{t.viewAllVideos || 'View All Videos'}</span>
              </button>
            </div>
            <div className="videos-content">
              <RecentVideosCarousel key={refreshKey} />
            </div>
          </section>
        </div>
      </main>

      {/* Right Side Menu */}
      <RightSideMenu
        isOpen={showRightMenu}
        onClose={() => setShowRightMenu(false)}
        user={user}
        currentPlan={creditStatus?.subscription_plan || 'free'}
        onOpenSubscription={handleOpenSubscription}
      />

      {/* Subscription Modal */}
      <SubscriptionModal
        isOpen={showSubscriptionModal}
        onClose={handleCloseSubscription}
        currentPlan={creditStatus?.subscription_plan || 'free'}
        onSelectPlan={handleSelectPlan}
      />
    </div>
  );
}

export default Dashboard;